import React, { useState, useEffect, useRef } from 'react';
import { useApiService } from '../../services/api';
import { 
  Upload, 
  FileText, 
  Download, 
  Trash2, 
  Plus,
  X,
  File,
  Image,
  FileImage,
  FileArchive,
  FileVideo,
  FileAudio
} from 'lucide-react';
import './AttachmentUpload.css';

const AttachmentUpload = ({ dealId, onAttachmentChange }) => {
  const apiService = useApiService();
  const componentRef = useRef(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [description, setDescription] = useState('');

  // Use ref to track if component is mounted
  useEffect(() => {
    componentRef.current = true;
    return () => {
      componentRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (dealId && componentRef.current) {
      loadAttachments();
    }
  }, [dealId]);

  useEffect(() => {
    if (componentRef.current) {
      console.log('showUploadForm state changed:', showUploadForm);
    }
  }, [showUploadForm]);

  useEffect(() => {
    if (componentRef.current) {
      console.log('selectedFile state changed:', selectedFile);
    }
  }, [selectedFile]);

  // Debug: Log when attachments change
  useEffect(() => {
    console.log('Attachments state changed:', attachments);
  }, [attachments]);

  const loadAttachments = async () => {
    try {
      setLoading(true);
      const response = await apiService.getDealAttachments(dealId);
      console.log('API Response:', response); // Debug log
      
      // The backend returns { attachments: [...] }, not { data: { attachments: [...] } }
      const attachments = response.attachments || [];
      console.log('Extracted attachments:', attachments); // Debug log
      
      setAttachments(attachments);
      if (onAttachmentChange) {
        onAttachmentChange(attachments);
      }
    } catch (error) {
      console.error('Error loading attachments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Upload toggle clicked, current state:', showUploadForm);
    
    setShowUploadForm(prevState => {
      const newState = !prevState;
      console.log('Setting new state from', prevState, 'to', newState);
      return newState;
    });
    
    // Force a re-render check
    setTimeout(() => {
      console.log('State after timeout:', showUploadForm);
    }, 100);
  };

  const handleFileSelect = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const fileExtension = file.name.toLowerCase().split('.').pop();
      const allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png'];
      const allowedMimeTypes = [
        'application/pdf',
        'image/jpeg',
        'image/jpg',
        'image/png'
      ];

      if (!allowedExtensions.includes(fileExtension) || !allowedMimeTypes.includes(file.type)) {
        alert('Invalid file type. Only PDF and image files (JPG, JPEG, PNG) are allowed.');
        e.target.value = ''; // Clear the input
        setSelectedFile(null);
        return;
      }

      console.log('File selected:', file.name);
      setSelectedFile(file);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!selectedFile) {
      alert('Please select a file to upload');
      return;
    }

    // Validate file type again before upload (backup validation)
    const fileExtension = selectedFile.name.toLowerCase().split('.').pop();
    const allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png'];
    const allowedMimeTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png'
    ];

    if (!allowedExtensions.includes(fileExtension) || !allowedMimeTypes.includes(selectedFile.type)) {
      alert('Invalid file type. Only PDF and image files (JPG, JPEG, PNG) are allowed.');
      setSelectedFile(null);
      return;
    }

    try {
      setUploading(true);
      console.log('Uploading file:', selectedFile.name);
      await apiService.uploadDealAttachment(dealId, selectedFile, description);
      
      // Reset form
      setSelectedFile(null);
      setDescription('');
      setShowUploadForm(false);
      
      // Reload attachments
      await loadAttachments();
      
      alert('File uploaded successfully!');
    } catch (error) {
      console.error('Error uploading file:', error);
      
      // Show more specific error message
      let errorMessage = 'Failed to upload file. Please try again.';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.error) {
        errorMessage = error.error;
      }
      
      alert(`Upload failed: ${errorMessage}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (e, attachmentId) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!window.confirm('⚠️ WARNING: This action cannot be undone. Are you sure you want to continue?')) {
      return;
    }

    try {
      console.log('Attempting to delete attachment:', attachmentId);
      const response = await apiService.deleteDealAttachment(dealId, attachmentId);
      
      console.log('Delete response:', response); // Debug log
      
      // Show detailed deletion status
      if (response && response.success) {
        if (response.s3Deleted && response.databaseDeleted) {
          alert('✅ File permanently deleted!');
        } else if (response.databaseDeleted && !response.s3Deleted) {
          alert('⚠️ File deleted.');
        } else if (response.databaseDeleted) {
          alert('✅ File deleted successfully!');
        } else {
          alert('❌ Deletion failed. Please try again.');
        }
      } else {
        console.error('Unexpected response format:', response);
        alert('❌ Deletion failed. Please try again.');
      }
      
      await loadAttachments();
    } catch (error) {
      console.error('Error deleting attachment:', error);
      
      // Show more specific error message
      let errorMessage = 'Failed to delete attachment. Please try again.';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.error) {
        errorMessage = error.error;
      }
      
      alert(`Delete failed: ${errorMessage}`);
    }
  };

  const handleDownload = async (e, attachment) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      // Check if it's a local:// URL (not supported)
      if (attachment.url && attachment.url.startsWith('local://')) {
        alert('⚠️ This file is not available for download. It was uploaded in development mode without S3 configuration.');
        return;
      }

      // For S3 URLs or when we need to get a presigned URL, use the download endpoint
      const response = await apiService.downloadDealAttachment(dealId, attachment.id);
      
      if (response && response.downloadUrl) {
        // Open the presigned URL in a new tab/window to download
        const link = document.createElement('a');
        link.href = response.downloadUrl;
        link.download = response.fileName || attachment.name;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (attachment.url && !attachment.url.startsWith('local://')) {
        // Fallback: if we have a direct URL (like a full S3 URL), use it
        const link = document.createElement('a');
        link.href = attachment.url;
        link.download = attachment.name;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert('⚠️ Unable to download file. Please try again later.');
      }
    } catch (error) {
      console.error('Error downloading attachment:', error);
      const errorMessage = error.details?.message || error.message || 'Failed to download file';
      alert(`❌ Download failed: ${errorMessage}`);
    }
  };

  const getFileIcon = (mimeType) => {
    if (mimeType.startsWith('image/')) {
      return <Image size={20} />;
    } else if (mimeType === 'application/pdf') {
      return <FileText size={20} />;
    } else if (mimeType.includes('word') || mimeType.includes('document')) {
      return <FileText size={20} />;
    } else if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) {
      return <FileText size={20} />;
    } else if (mimeType.startsWith('video/')) {
      return <FileVideo size={20} />;
    } else if (mimeType.startsWith('audio/')) {
      return <FileAudio size={20} />;
    } else if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('tar')) {
      return <FileArchive size={20} />;
    } else {
      return <File size={20} />;
    }
  };

  const formatFileSize = (size) => {
    if (size < 1024) {
      return `${size} B`;
    } else if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    } else {
      return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="attachment-upload-container">
        <div className="attachments-loading">
          <div>Loading attachments...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="attachment-upload-container">
      {/* Debug Info */}
      
      
      <div className="attachments-header">
        <h3>Attachments</h3>
        <button
          className="upload-toggle-btn"
          onClick={handleUploadToggle}
          type="button"
        >
          <Plus size={16} />
          {showUploadForm ? 'Cancel Upload' : 'Upload File'}
        </button>
      </div>

      {showUploadForm && (
        <div 
          key={`upload-form-${showUploadForm}`}
          className="upload-form" 
          style={{ display: 'block', visibility: 'visible', opacity: 1 }}
        >
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="file-input">Select File</label>
              <input
                id="file-input"
                type="file"
                className="file-input"
                onChange={handleFileSelect}
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/jpg,image/png"
                onClick={(e) => e.stopPropagation()}
              />
              <small style={{ color: '#6c757d', fontSize: '12px', marginTop: '4px' }}>
                Maximum file size: 50MB. Supported file types: PDF, JPG, JPEG, PNG only.
              </small>
            </div>
            <div className="form-group">
              <label htmlFor="description-input">Description (Optional)</label>
              <input
                id="description-input"
                type="text"
                className="description-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter file description..."
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
          <div className="form-actions">
            <button
              className="cancel-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowUploadForm(false);
                setSelectedFile(null);
                setDescription('');
              }}
              type="button"
            >
              Cancel
            </button>
            <button
              className="upload-btn"
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              type="button"
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </div>
      )}

      <div className="attachments-list">
        {/* Debug: Show attachments state */}
        <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
          Debug: {attachments.length} attachments loaded
        </div>
        
        {attachments.length === 0 ? (
          <div className="no-attachments">
            <FileText size={48} />
            <p>No attachments uploaded yet</p>
            <p>Click "Upload File" to add documents and files to this deal</p>
          </div>
        ) : (
          attachments.map((attachment) => {
            console.log('Rendering attachment:', attachment); // Debug log
            return (
              <div key={attachment.id} className="attachment-item">
                <div className="attachment-info">
                  <div className="file-icon">
                    {getFileIcon(attachment.type)}
                  </div>
                  <div className="attachment-details">
                    <div className="file-name">{attachment.name}</div>
                    {attachment.description && (
                      <div className="file-description">{attachment.description}</div>
                    )}
                    <div className="file-meta">
                      <span>{formatFileSize(attachment.size)}</span>
                      <span>•</span>
                      <span>{formatDate(attachment.uploadedAt)}</span>
                    </div>
                  </div>
                </div>
              <div className="attachment-actions">
                <button
                  className="action-btn download-btn"
                  onClick={(e) => handleDownload(e, attachment)}
                  title="Download"
                  type="button"
                >
                  <Download size={16} />
                </button>
                <button
                  className="action-btn delete-btn"
                  onClick={(e) => handleDelete(e, attachment.id)}
                  title="Delete"
                  type="button"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AttachmentUpload;
